import { useEffect, useRef, useState } from 'react';
import { PanelHeaderButton } from '~/design-system/components/v1_5/Layout';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './BrowserBar.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface BrowserBarProps {
  /** The address, as written. Re-seeds the field whenever it changes underneath. */
  url: string;
  /**
   * Whether the site answered when it was last checked. The dot is the whole of it — there is no
   * text status, because the pane's own bar above already carries `checked 5m ago`.
   */
  live?: boolean;
  /**
   * A new address was entered. Fires on Enter, and only for an address that actually changed —
   * Escape reverts and never calls this, exactly as every other field in v1.5 behaves.
   */
  onNavigate?: (url: string) => void;
  onReload?: () => void;
  onOpenExternal?: () => void;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The website's own band, under the pane's.
 *
 * A website used to draw a 34px bar INSIDE its frame, on the artifact's own permanently-dark
 * palette, because the artifact was a card floating on the mat and its chrome belonged to it. The
 * card is gone — a website now fills the pane the way a document does — and with it goes the reason
 * that bar was the artifact's. This one is the PANE's: it follows the app's theme like every other
 * band, it sits under the preview header rather than inside the content, and it does not scale or
 * scroll with the page below it.
 *
 * Reload leads, the address fills, open-in-a-new-tab trails. That order is not this file's
 * invention and is deliberately not v1.5's usual content-left-controls-right band: it is the order
 * every browser uses, and a thing that is pretending to be browser chrome should be laid out like
 * browser chrome or it is just a toolbar with a URL in it.
 */
export function BrowserBar({
  url,
  live = true,
  onNavigate,
  onReload,
  onOpenExternal,
  theme: themeProp,
  className,
}: BrowserBarProps) {
  const theme = useV15Theme(themeProp);
  const [draft, setDraft] = useState(url);
  const inputRef = useRef<HTMLInputElement>(null);

  // The address can change without this field being touched — a reload, a different site opened
  // from the Directory — and a stale draft would then be showing somewhere the pane is not.
  useEffect(() => setDraft(url), [url]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== url) onNavigate?.(next);
    else setDraft(url);
    inputRef.current?.blur();
  };

  return (
    <div className={cn(themes[theme], styles.bar, className)}>
      <PanelHeaderButton
        icon="loop-right"
        label="Reload"
        theme={theme}
        onClick={onReload}
        className={styles.action}
      />

      <div className={styles.field}>
        {/* The status is a colour, so it needs a name of its own or it is invisible to anything not
            looking at it. It sits where a browser puts its lock, which is the one slot in an address
            bar the eye already reads as "is this connection all right". */}
        <span
          className={styles.dot}
          data-live={live}
          role="img"
          aria-label={live ? 'Site responded' : 'Site did not respond'}
        />
        <input
          ref={inputRef}
          className={styles.url}
          type="text"
          inputMode="url"
          spellCheck={false}
          autoComplete="off"
          value={draft}
          aria-label="Address"
          onChange={(e) => setDraft(e.target.value)}
          // Selecting the whole address on focus is what every browser does, and it is the right
          // default: an address is replaced far more often than it is edited in the middle.
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(url);
              inputRef.current?.blur();
            }
            // The caret owns its arrows and its ⌘A; whatever is listening upstairs does not.
            e.stopPropagation();
          }}
          // NOT on blur, unlike the rename fields. Clicking away from a half-typed address means
          // "never mind" far more often than it means "go there", and going somewhere is not a
          // change you can glance at and undo.
          onBlur={() => setDraft(url)}
        />
      </div>

      <PanelHeaderButton
        icon="arrow-right-up"
        label="Open in a new tab"
        theme={theme}
        onClick={onOpenExternal}
        className={styles.action}
      />
    </div>
  );
}
